import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatEUR } from "@/lib/api";

const SORTS = [
  { key: "sales", label: "Valore venduto" },
  { key: "customers", label: "Clienti" },
  { key: "commissions", label: "Provvigioni" },
];

const shiftMonth = (m, delta) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(Date.UTC(y, mo - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
};
const monthLabel = (m) => new Date(`${m}-01T12:00:00Z`).toLocaleDateString("it-IT", { month: "long", year: "numeric" });
const current = new Date().toISOString().slice(0, 7);

export const Leaderboard = ({ api }) => {
  const [month, setMonth] = useState(current);
  const [sort, setSort] = useState("sales");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-leaderboard", month],
    queryFn: async () => (await api.get("/admin/leaderboard", { params: { month } })).data,
  });
  const items = [...(data?.items || [])].sort((a, b) => b[sort] - a[sort] || b.sales - a.sales || b.commissions - a.commissions);

  return (
    <div className="bg-white border border-[#E2DDD5] rounded-xl p-5 min-w-0 max-w-full overflow-hidden" data-testid="leaderboard">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Trophy size={16} className="text-[#C69214]" />
          <div className="font-semibold">Classifica rivenditori</div>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="leaderboard-prev"><ChevronLeft size={14} /></Button>
          <div className="w-36 text-center text-sm font-medium capitalize" data-testid="leaderboard-month">{monthLabel(month)}</div>
          <Button variant="outline" size="icon" className="h-8 w-8" disabled={month >= current} onClick={() => setMonth(shiftMonth(month, 1))} data-testid="leaderboard-next"><ChevronRight size={14} /></Button>
        </div>
      </div>
      <div className="mt-3 inline-flex rounded-lg border border-[#E2DDD5] p-0.5 bg-[#F9F8F6]">
        {SORTS.map((s) => (
          <button key={s.key} onClick={() => setSort(s.key)} data-testid={`leaderboard-sort-${s.key}`}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${sort === s.key ? "bg-[#1E2A38] text-white" : "text-slate-600 hover:text-[#1E2A38]"}`}>
            {s.label}
          </button>
        ))}
      </div>
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wider text-slate-500 bg-[#F2F0EB]">
            <tr><th className="text-left px-3 py-2 w-10">#</th><th className="text-left px-3 py-2">Rivenditore</th><th className="text-right px-3 py-2">Clienti</th><th className="text-right px-3 py-2">Valore venduto</th><th className="text-right px-3 py-2">Provvigioni</th></tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">Caricamento…</td></tr>}
            {!isLoading && items.length === 0 && <tr><td colSpan={5} className="px-3 py-4 text-center text-slate-500">Nessun rivenditore.</td></tr>}
            {items.map((r, i) => (
              <tr key={r.reseller_id} className="border-t border-[#E2DDD5]" data-testid={`leaderboard-row-${r.reseller_id}`}>
                <td className="px-3 py-2 font-semibold" style={{ color: i < 3 ? "#C69214" : undefined }}>{i + 1}</td>
                <td className="px-3 py-2"><Link to={`/admin/resellers/${r.reseller_id}`} className="hover:text-[#B84A2B]">{r.name}</Link><div className="text-xs text-slate-500 font-mono">{r.codice_reseller}</div></td>
                <td className="px-3 py-2 text-right font-mono">{r.customers}</td>
                <td className="px-3 py-2 text-right font-mono">{formatEUR(r.sales)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatEUR(r.commissions)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
